import React, { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouterProps } from 'react-router';
import { Form } from 'antd';
import { PaceTaskScreen } from 'providence-types';
import { HttpService } from '@core/services';
import { ProjectHttpService } from '@core/services/http';
import { useDidUpdateEffect, useSaveChanged } from '@core/utils/hooks';
import {
  canUserEditByProjectStatusAndRoles,
  getIdParamFromUrl,
  isValidForm,
} from '@core/utils/methods';
import { FormProps } from '@models/interfaces';
import {
  EditablePage,
  getPaceTaskDifference,
  NotificationsLoader,
  paceTaskMapper,
  PaceTaskTable,
  Panel,
  ViewElement,
} from '@shared/components';
import { PrimaryButton, ProgressStatus, ProjectStatus } from '@shared/modules';
import { CommonActions, ProjectActions } from '@store/actions';
import {
  AccountingSelectors,
  PaceTasksSelectors,
  ProjectSelectors,
  UserSelectors,
} from '@store/selectors';

const { useForm } = Form;

const AccountingSummaryView: FC<FormProps> = ({
  isEditing = false,
  toggleEditing,
}: PropsWithChildren<FormProps>) => {
  const dispatch = useDispatch();

  const [paceTaskForm] = useForm();

  const [projectId] = getIdParamFromUrl();

  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);

  const {
    FALocationNumber,
    projectBundleId,
    projectType,
    projectWorkType,
    projectSubtype,
    projectStatus,
    progressStatus,
    ATnTPM,
    siteAcqVendor,
    providenceAgent,
    civilVendor,
    notes,
  } = useSelector(AccountingSelectors.getProjectData);
  const { engineeringNotes, siteNotes, permittingNotes, leasingNotes } =
    useSelector(AccountingSelectors.getNotesData);
  const arePaceTasksChanged = useSelector(
    PaceTasksSelectors.arePaseTasksChanged,
  );
  const currentPaceTasks = useSelector(PaceTasksSelectors.getPaceTasks);
  const roles = useSelector(UserSelectors.getUserRoles);

  useEffect(
    () => (): void => {
      dispatch(CommonActions.setHasUnsubmittedData.done(false));
    },

    [],
  );

  useDidUpdateEffect(() => {
    dispatch(CommonActions.setHasUnsubmittedData.done(arePaceTasksChanged));
  }, [arePaceTasksChanged]);

  const onSubmit = async (): Promise<void> => {
    const [paceTasksData, keys] = getPaceTaskDifference(
      currentPaceTasks,
      paceTaskForm,
      roles,
    );

    if (await isValidForm(paceTaskForm, keys)) {
      try {
        setSubmitDisabled(true);

        await HttpService.getHttpRequests(
          ProjectHttpService,
        ).updateProjectPaceTasks(
          projectId,
          PaceTaskScreen.Accounting,
          paceTasksData,
        );

        toggleEditing?.();

        dispatch(
          ProjectActions.updateProjectPaceTaskFieldsFromSummaryPageTable.done(
            paceTasksData,
          ),
        );

        dispatch(ProjectActions.setArePACETasksChanged.done(false));

        NotificationsLoader.notificationSuccess(
          `PACE tasks have been updated!`,
        );

        setSubmitDisabled(false);
      } catch (e) {
        console.error(e);

        setSubmitDisabled(false);
      }
    }
  };

  const onCancel = (): void => {
    paceTaskForm.setFieldsValue(paceTaskMapper(currentPaceTasks));
    dispatch(ProjectActions.setArePACETasksChanged.done(false));

    toggleEditing?.();
  };

  useSaveChanged(isEditing, onSubmit, onCancel);

  return (
    <>
      <div className="prov-accounting-summary">
        <div className="prov-accounting-summary__information">
          <Panel header="Project Information" className="project">
            <ViewElement label="FA Location Number" value={FALocationNumber} />
            <ViewElement label="Project Bundle ID" value={projectBundleId} />
            <ViewElement label="Project Type" value={projectType} />
            <ViewElement label="Project Work Type" value={projectWorkType} />
            <ViewElement label="Project Subtype" value={projectSubtype} />
            <ViewElement
              label="Project Status"
              element={<ProjectStatus status={projectStatus} />}
            />
            <ViewElement
              label="Progress Status"
              element={<ProgressStatus status={progressStatus} />}
            />
            <ViewElement label="AT&T PM" value={ATnTPM} />
            <ViewElement label="Site Acq Vendor" value={siteAcqVendor} />
            <ViewElement label="Providence Agent" value={providenceAgent} />
            <ViewElement label="Civil Vendor" value={civilVendor} />
            <ViewElement label="Project Notes" value={notes} />
          </Panel>

          <Panel header="Notes" className="notes">
            <ViewElement label="Site Notes" value={siteNotes} />
            <ViewElement label="Engineering Notes" value={engineeringNotes} />
            <ViewElement label="Leasing Notes" value={leasingNotes} />
            <ViewElement label="Permitting Notes" value={permittingNotes} />
          </Panel>
        </div>
        <PaceTaskTable
          isEditing={isEditing}
          data={currentPaceTasks || []}
          form={paceTaskForm}
        />
      </div>
      {isEditing && (
        <div className="prov-project-summary__btns">
          <div className="action-wrap">
            <PrimaryButton
              title="Submit"
              type="primary"
              disabled={submitDisabled}
              onClick={onSubmit}
            />
            <PrimaryButton title="Cancel" type="default" onClick={onCancel} />
          </div>
        </div>
      )}
    </>
  );
};

export const AccountingSummary: FC<RouterProps> = ({
  history,
}: PropsWithChildren<RouterProps>) => {
  const userRoles = useSelector(UserSelectors.getUserRoles);
  const projectStatus = useSelector(ProjectSelectors.getProjectStatus);

  return (
    <EditablePage
      editableComponent={AccountingSummaryView}
      history={history}
      canUserEdit={canUserEditByProjectStatusAndRoles(projectStatus, userRoles)}
    />
  );
};
